// src/components/job-engine/useDeadLetters.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getDeadLetters } from "@/api/jobEngine";
import type { DeadLetter, DeadLettersQuery } from "@/api/jobEngine";

export interface UseDeadLettersResult {
  items: DeadLetter[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDeadLetters(params: DeadLettersQuery): UseDeadLettersResult {
  const [items, setItems] = useState<DeadLetter[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getDeadLetters(params, ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setItems(r.items ?? []);
        setTotalCount(r.totalCount ?? 0);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load dead letters");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return { items, totalCount, isLoading, error, refetch };
}
